import { useState, useEffect } from "react";
import api from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { useNavigate } from "react-router-dom";
import { Users, CheckCircle, Clock, TrendingUp, Shield, Zap, Search, FileText, Download, Link2 } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { toast } from "sonner";

const CHART_COLORS = ["#4A7AB5", "#F5A623", "#8CAED6", "#F9C77A", "#5BA88E"];

export default function CoachDashboard() {
    const [stats, setStats] = useState(null);
    const [users, setUsers] = useState([]);
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        Promise.all([
            api.get("/coach/dashboard"),
            api.get("/coach/users")
        ]).then(([dashRes, usersRes]) => {
            setStats(dashRes.data);
            setUsers(usersRes.data);
        }).catch(console.error).finally(() => setLoading(false));
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const filteredUsers = users.filter(u => {
        const q = search.toLowerCase();
        return !q || u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q) || u.organization?.toLowerCase().includes(q);
    });

    const archetypeData = stats ? Object.entries(stats.archetype_distribution || {}).map(([name, count]) => ({
        name, value: count
    })) : [];

    const handleExportPDF = async (userId) => {
        try {
            const response = await api.get(`/reports/${userId}/pdf`, { responseType: "blob" });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `report_${userId}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (err) { console.error("PDF download failed", err); }
    };

    const copyAssessmentLink = async (userId) => {
        try {
            const { data } = await api.get(`/coach/users/${userId}/link`);
            await navigator.clipboard.writeText(data.link);
            toast.success("Assessment link copied!", { description: data.link });
        } catch (err) { toast.error("Failed to get assessment link"); }
    };

    if (loading) return (
        <div className="flex items-center justify-center h-full">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" />
        </div>
    );

    const statCards = [
        { label: "Total Users", value: stats?.total_users || 0, icon: Users, color: "#4A7AB5" },
        { label: "Completed", value: stats?.total_completed || 0, icon: CheckCircle, color: "#5BA88E" },
        { label: "Pending", value: stats?.total_pending || 0, icon: Clock, color: "#F5A623" },
        { label: "Avg. Score", value: stats?.average_score || 0, icon: TrendingUp, color: "#8CAED6" },
        { label: "Avg. APS", value: stats?.average_aps || 0, icon: Zap, color: "#F9C77A" },
        { label: "Avg. Defense", value: stats?.average_defense || 0, icon: Shield, color: "#94A3B8" },
    ];

    return (
        <div data-testid="coach-dashboard" className="p-4 sm:p-6 lg:p-8 space-y-6 sm:space-y-8">
            <div>
                <h1 className="text-2xl lg:text-3xl font-bold text-foreground" style={{ fontFamily: "Outfit" }}>
                    Coach Dashboard
                </h1>
                <p className="text-sm text-muted-foreground mt-1">Your assessment users overview</p>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                {statCards.map((s, i) => (
                    <Card key={s.label} className="border-border bg-card">
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                                <div className="p-2 rounded-lg" style={{ backgroundColor: s.color + "15" }}>
                                    <s.icon className="h-4 w-4" style={{ color: s.color }} />
                                </div>
                                <div>
                                    <p className="text-xs text-muted-foreground">{s.label}</p>
                                    <p className="text-lg font-bold text-foreground">{s.value}</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                ))}
            </div>

            {/* Archetype Distribution */}
            {archetypeData.length > 0 && (
                <Card className="border-border bg-card">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-base font-semibold" style={{ fontFamily: "Outfit" }}>Archetype Mix</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="flex items-center gap-8">
                            <div className="h-48 w-48">
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie data={archetypeData} cx="50%" cy="50%" innerRadius={40} outerRadius={70} dataKey="value" stroke="none">
                                            {archetypeData.map((entry, i) => (
                                                <Cell key={`pie-${entry.name}`} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                                            ))}
                                        </Pie>
                                        <Tooltip contentStyle={{ backgroundColor: "#FFFFFF", border: "1px solid #E2E8F0", borderRadius: "8px", color: "#1E293B", boxShadow: "0 4px 12px rgba(0,0,0,0.08)" }} />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>
                            <div className="space-y-2">
                                {archetypeData.map((a, i) => (
                                    <div key={a.name} className="flex items-center gap-2">
                                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }} />
                                        <span className="text-sm text-muted-foreground">{a.name} ({a.value})</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Users Table */}
            <Card className="border-border bg-card">
                <CardHeader className="pb-3">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <CardTitle className="text-base font-semibold" style={{ fontFamily: "Outfit" }}>My Users</CardTitle>
                        <div className="flex gap-3">
                            <div className="relative w-full sm:w-64">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                <Input data-testid="coach-search-input" placeholder="Search..." value={search}
                                    onChange={(e) => setSearch(e.target.value)} className="pl-9 bg-background border-border" />
                            </div>
                            <Button data-testid="add-user-btn" onClick={() => navigate("/coach/users")} style={{ backgroundColor: "#4A7AB5" }}>
                                Add User
                            </Button>
                        </div>
                    </div>
                </CardHeader>
                <CardContent className="p-0">
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow className="border-border">
                                    <TableHead className="text-muted-foreground">Name</TableHead>
                                    <TableHead className="text-muted-foreground">Organization</TableHead>
                                    <TableHead className="text-muted-foreground">Status</TableHead>
                                    <TableHead className="text-muted-foreground">Score</TableHead>
                                    <TableHead className="text-muted-foreground">Archetype</TableHead>
                                    <TableHead className="text-muted-foreground">APS</TableHead>
                                    <TableHead className="text-muted-foreground">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {filteredUsers.map((u) => {
                                    const a = u.assessment;
                                    const status = a?.status || "not_started";
                                    const score = a?.scores?.total_weighted_score;
                                    const archetype = a?.scores?.archetype?.name;
                                    const aps = a?.scores?.aps?.score;
                                    return (
                                        <TableRow key={u.id} className="border-border hover:bg-secondary/50">
                                            <TableCell>
                                                <div>
                                                    <p className="font-medium text-foreground text-sm">{u.name}</p>
                                                    <p className="text-xs text-muted-foreground">{u.email}</p>
                                                </div>
                                            </TableCell>
                                            <TableCell className="text-sm text-muted-foreground">{u.organization}</TableCell>
                                            <TableCell>
                                                <Badge variant={status === "completed" ? "default" : status === "in_progress" ? "secondary" : "outline"}
                                                    className={status === "completed" ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30" : ""}>
                                                    {status.replace("_", " ")}
                                                </Badge>
                                            </TableCell>
                                            <TableCell className="text-sm font-medium text-foreground">
                                                {score != null ? score.toFixed(1) : "-"}
                                            </TableCell>
                                            <TableCell className="text-xs text-muted-foreground max-w-[140px] truncate">{archetype || "-"}</TableCell>
                                            <TableCell className="text-sm text-muted-foreground">{aps != null ? aps.toFixed(1) : "-"}</TableCell>
                                            <TableCell>
                                                <div className="flex gap-1">
                                                    {status !== "completed" && (
                                                        <Button data-testid={`copy-link-${u.id}`} variant="ghost" size="sm"
                                                            onClick={() => copyAssessmentLink(u.id)}
                                                            title="Copy assessment link"
                                                            className="text-[#F5A623] hover:text-[#F5A623]/80">
                                                            <Link2 className="h-4 w-4" />
                                                        </Button>
                                                    )}
                                                    {status === "completed" && (
                                                        <>
                                                            <Button data-testid={`view-report-${u.id}`} variant="ghost" size="sm"
                                                                onClick={() => navigate(`/coach/reports/${u.id}`)}
                                                                className="text-primary hover:text-primary/80">
                                                                <FileText className="h-4 w-4" />
                                                            </Button>
                                                            <Button variant="ghost" size="sm" onClick={() => handleExportPDF(u.id)}
                                                                className="text-muted-foreground hover:text-foreground">
                                                                <Download className="h-4 w-4" />
                                                            </Button>
                                                        </>
                                                    )}
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                                {filteredUsers.length === 0 && (
                                    <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">No users found</TableCell></TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
